import React, { useEffect, useState, useMemo } from 'react';
import { Card, MetricCard, Plate } from '../components/Card';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getSalesSummary, getSalesOrders, getSalesTopCustomers } from '../api';

const fmt = n => n ? n.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽' : '0 ₽';

const STATUS_COLOR = {
  'Отгружен': '#0e9f6e', 'Выполнен': '#0e9f6e', 'Закрыт': '#0e9f6e',
  'В работе': '#e08a00', 'Подтверждён': '#00a7dc',
  'Отменён': '#e04848', 'Отказ': '#e04848',
};

export default function Sales() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState('chart');

  useEffect(() => { getSalesSummary().then(setSummary).catch(() => {}); }, []);
  useEffect(() => {
    getSalesOrders(days).then(setOrders).catch(() => {});
    getSalesTopCustomers(days).then(setTopCustomers).catch(() => {});
  }, [days]);

  const chartData = useMemo(() => {
    const byDay = {};
    orders.forEach(o => {
      const d = o.date?.slice(0, 10) || '';
      if (!d) return;
      byDay[d] = (byDay[d] || 0) + o.amount;
    });
    return Object.entries(byDay).sort().map(([date, revenue]) => ({ date: date.slice(5), revenue }));
  }, [orders]);

  const statuses = useMemo(() => {
    const s = new Set(orders.map(o => o.status).filter(Boolean));
    return [...s].sort();
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let result = orders;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(o =>
        (o.customer || '').toLowerCase().includes(s) ||
        (o.number || '').toLowerCase().includes(s)
      );
    }
    if (statusFilter) {
      result = result.filter(o => o.status === statusFilter);
    }
    return result;
  }, [orders, search, statusFilter]);

  const totalFilteredRevenue = filteredOrders.reduce((s, o) => s + o.amount, 0);

  return (
    <div>
      <div className="kp-hero" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Plate>МойСклад · продажи</Plate>
          <h1 style={{ margin: 0 }}>Продажи</h1>
        </div>
        <select value={days} onChange={e => setDays(+e.target.value)}
          style={{ background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)', padding: '7px 12px', borderRadius: 8, fontSize: 13 }}>
          <option value={7}>7 дней</option>
          <option value={30}>30 дней</option>
          <option value={60}>60 дней</option>
          <option value={90}>90 дней</option>
        </select>
      </div>

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
          {[['Сегодня', 'today'], ['Неделя', 'week'], ['Месяц', 'month']].map(([label, key]) => (
            <MetricCard key={key} title={label}
              value={fmt(summary[key]?.revenue)}
              sub={`${summary[key]?.orders} заказов`}
              color="var(--green)" />
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['chart', '📊 График'], ['customers', '🏆 Топ клиенты'], ['orders', '📋 Заказы']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: activeTab === id ? 700 : 500, fontSize: 13,
            background: activeTab === id ? 'var(--accent)' : 'var(--bg2)',
            color: activeTab === id ? '#fff' : 'var(--text1)',
          }}>{label}</button>
        ))}
      </div>

      {/* Chart tab */}
      {activeTab === 'chart' && (
        <Card title={`Динамика выручки — последние ${days} дней`}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
              <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}к`} tick={{ fill: 'var(--text2)', fontSize: 11 }} />
              <Tooltip
                formatter={v => fmt(v)}
                contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8 }}
              />
              <Area type="monotone" dataKey="revenue" stroke="var(--accent)" fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Top customers tab */}
      {activeTab === 'customers' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
          <Card title={`Топ клиентов по выручке — ${days} дней`}>
            {topCustomers.length ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={topCustomers} layout="vertical" margin={{ left: 0, right: 30 }}>
                  <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}к`} tick={{ fill: 'var(--text2)', fontSize: 10 }} />
                  <YAxis type="category" dataKey="customer" width={160}
                    tick={{ fill: 'var(--text1)', fontSize: 10 }}
                    tickFormatter={v => v.length > 22 ? v.slice(0, 22) + '…' : v} />
                  <Tooltip
                    formatter={(v, n, p) => [fmt(v) + ` (${p.payload.orders} заказов)`, 'Выручка']}
                    contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                    {topCustomers.map((_, i) => (
                      <Cell key={i} fill="var(--accent)" fillOpacity={1 - i * 0.05} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ padding: 40, color: 'var(--text2)', textAlign: 'center' }}>Нет данных</div>
            )}
          </Card>
          <Card title="Список">
            <div style={{ overflowY: 'auto', maxHeight: 340 }}>
              {topCustomers.map((c, i) => (
                <div key={i} style={{
                  padding: '10px 0',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{
                      minWidth: 22, height: 22, borderRadius: 11,
                      background: i < 3 ? 'var(--accent)' : 'var(--bg3)',
                      color: i < 3 ? '#fff' : 'var(--text2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, flexShrink: 0,
                    }}>{i + 1}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.customer}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{c.orders} заказов</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)', flexShrink: 0 }}>
                    {fmt(c.revenue)}
                  </div>
                </div>
              ))}
              {!topCustomers.length && <div style={{ padding: 20, color: 'var(--text2)', textAlign: 'center' }}>Нет данных</div>}
            </div>
          </Card>
        </div>
      )}

      {/* Orders tab */}
      {activeTab === 'orders' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              placeholder="🔍 Поиск по клиенту или номеру…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                background: 'var(--bg2)', color: 'var(--text1)',
                border: '1px solid var(--border)', padding: '8px 14px',
                borderRadius: 8, fontSize: 13, width: 260, outline: 'none',
              }}
            />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ background: 'var(--bg2)', color: 'var(--text)', border: '1px solid var(--border)', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
              <option value="">Все статусы</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text2)', marginLeft: 4 }}>
              {filteredOrders.length} заказов · {fmt(totalFilteredRevenue)}
            </span>
          </div>

          <Card title={`Заказы (${filteredOrders.length})`}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Дата', 'Номер', 'Клиент', 'Статус', 'Сумма'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.slice(0, 200).map((o, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '9px 12px', color: 'var(--text2)', fontSize: 12, whiteSpace: 'nowrap' }}>{o.date?.slice(0, 10)}</td>
                      <td style={{ padding: '9px 12px', fontSize: 12, fontFamily: 'monospace' }}>{o.number}</td>
                      <td style={{ padding: '9px 12px', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.customer}</td>
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{
                          background: `${STATUS_COLOR[o.status] || 'var(--accent)'}22`,
                          color: STATUS_COLOR[o.status] || 'var(--text2)',
                          padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        }}>{o.status}</span>
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: 'var(--green)', whiteSpace: 'nowrap' }}>{fmt(o.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!filteredOrders.length && (
                <div style={{ padding: 24, color: 'var(--text2)', textAlign: 'center' }}>
                  {search || statusFilter ? 'Нет заказов по фильтру' : 'Нет данных'}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
